'use es6';

import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import unescapedText from 'I18n/utils/unescapedText';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import headerImageUrl from 'bender-url!ecosystem-marketplace-solutions-base/img/city-pages-header-image.png';
import socialImageUrl from 'bender-url!ecosystem-marketplace-solutions-base/img/city-pages-social-image.svg';
import { Text } from 'foundations-components/components/Text';
import { Image } from 'foundations-components/transitional/components/Image';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import Immutable from 'immutable';
import CanonicalLink from 'marketplace-ui-common/components/elements/CanonicalLink';
import MetaDescription from 'marketplace-ui-common/components/elements/MetaDescription';
import MetaOpenGraph from 'marketplace-ui-common/components/elements/MetaOpenGraph';
import MetaRobots from 'marketplace-ui-common/components/elements/MetaRobots';
import MetaTwitter from 'marketplace-ui-common/components/elements/MetaTwitter';
import DocumentHead from 'marketplace-ui-common/components/portals/DocumentHead';
import PrerenderReady from 'marketplace-ui-common/components/prerender/PrerenderReady';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import MainSection from 'marketplace-ui-common/layout/components/MainSection';
import MainSectionInner from 'marketplace-ui-common/layout/components/MainSectionInner';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import * as RequestState from 'marketplace-ui-common/requestStates/RequestState';
import { isFulfilled, isSucceeded, isUninitialized } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import HSPageViewTracker from 'marketplace-ui-common/tracking/components/HSPageViewTracker';
import * as CityCollections from 'marketplace-ui-solutions-core/constants/CityCollection';
import * as CityCollectionRoutes from 'marketplace-ui-solutions-core/constants/routes/CityCollectionRoute';
import { ProfileListing } from 'marketplace-ui-solutions-core/records/ProfileListing';
import { UsageScreenProvider } from 'marketplace-ui-tracking';
import Raven from 'raven-js';
import { Fragment, useCallback, useEffect, useMemo, useReducer } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { marketplaceSearchProfiles } from '../clients/marketplaceSearchClient';
import GenericError from '../components/error/GenericError';
import { ResultsCallout } from '../components/results/ResultsCallout';
import { ResultsGrid } from '../components/results/ResultsGrid';
import UsageViewTracker from '../components/tracking/UsageViewTracker';
import * as CityCollectionQuery from '../constants/search/CityCollectionQuery';
import { PublicAppComponent, getCanonicalAppContext } from '../selectors/app';
import { getFullLocationPathname, getFullLocationUrl, getSignUpUrl } from '../utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CITY_COLLECTION_KEYS = Object.keys(CityCollections);
const START_GRADIENT_COLOR = '#00497D';
const END_GRADIENT_COLOR = '#0FBFBF';
const routeCollectionMap = {};
const getCityCollectionFromRoute = route => {
  let collection = routeCollectionMap[route];
  if (!collection) {
    const collectionKey = CITY_COLLECTION_KEYS.find(key => CityCollectionRoutes[key] === route);
    if (collectionKey) {
      collection = routeCollectionMap[route] = CityCollections[collectionKey];
    }
  }
  return collection;
};
const HeaderSection = styled.header.withConfig({
  displayName: "CityCollection__HeaderSection",
  componentId: "sc-1mvjq0i-0"
})(["padding:", ";background-image:linear-gradient( 193.97deg,", ",", " );color:", ";border-bottom-left-radius:75% 50%;border-bottom-right-radius:75% 50%;@media (min-width:768px){border-bottom-left-radius:50% 25%;border-bottom-right-radius:50% 25%;}@media (max-width:768px){padding-bottom:", ";border-bottom-left-radius:100% 20%;border-bottom-right-radius:100% 20%;}"], ({
  theme
}) => `${theme.space['1000']} ${theme.space['1100']} ${theme.space['1400']} ${theme.space['1100']}`, START_GRADIENT_COLOR, END_GRADIENT_COLOR, ({
  theme
}) => theme.color['text-core-on-fill-default'], ({
  theme
}) => theme.space['1000']);
const ResultsErrorComponent = () => /*#__PURE__*/_jsx(NavMarker, {
  name: "CITY_COLLECTION_ERROR",
  children: /*#__PURE__*/_jsx(GenericError, {})
});
const ResultsLoadingComponent = () => /*#__PURE__*/_jsx(LoadingSpinner, {});
const RESULTS_REQUESTED = 'RESULTS_REQUESTED';
const RESULTS_RECEIVED = 'RESULTS_RECEIVED';
const RESULTS_FAILED = 'RESULTS_FAILED';
const initialState = {
  requestState: RequestState.UNINITIALIZED,
  results: Immutable.List(),
  total: 0
};
const reducer = (state, action) => {
  switch (action.type) {
    case RESULTS_REQUESTED:
      {
        return Object.assign({}, state, {
          requestState: RequestState.PENDING
        });
      }
    case RESULTS_RECEIVED:
      {
        const {
          items,
          total
        } = action.payload;
        return Object.assign({}, state, {
          requestState: RequestState.SUCCEEDED,
          results: Immutable.List(items.map(item => new ProfileListing(item))),
          total
        });
      }
    case RESULTS_FAILED:
      {
        return Object.assign({}, state, {
          requestState: RequestState.FAILED
        });
      }
    default:
      return state;
  }
};
const CityCollection = () => {
  const {
    pathname
  } = useLocation();
  const {
    city: cityParam
  } = useParams();
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    requestState,
    results,
    total
  } = state;
  const canonicalAppContext = useSelector(getCanonicalAppContext);
  const lang = getLang();
  const cityCollection = useMemo(() => {
    return getCityCollectionFromRoute(cityParam);
  }, [cityParam]);
  const cityText = useMemo(() => {
    return unescapedText(`ecosystem-marketplace-solutions-ui.cityCollection.city.${cityCollection}`);
  }, [cityCollection]);
  const metaDescriptionText = useMemo(() => {
    return unescapedText('ecosystem-marketplace-solutions-ui.cityCollection.metaDescription', {
      city: cityText
    });
  }, [cityText]);
  const canonicalUrl = useMemo(() => getFullLocationUrl({
    pathname
  }, canonicalAppContext, lang), [canonicalAppContext, lang, pathname]);
  const noFollowIndex = useMemo(() => {
    if (isSucceeded(requestState)) {
      return total < 3;
    }
    return false;
  }, [requestState, total]);
  const prerenderStatusCode = useMemo(() => {
    if (isSucceeded(requestState) && total === 0) {
      return 404;
    }
    return undefined;
  }, [requestState, total]);
  const getUsageEventProperties = useCallback((extras = {}) => {
    return Object.assign({}, extras, {
      screen: 'city page',
      subscreen: cityCollection,
      totalResults: total
    });
  }, [cityCollection, total]);
  const profilesSearchClient = marketplaceSearchProfiles;
  useEffect(() => {
    document.title = I18n.text('ecosystem-marketplace-solutions-ui.cityCollection.htmlTitle', {
      city: cityText
    });
  }, [cityText]);
  useEffect(() => {
    if (isUninitialized(requestState)) {
      const searchQuery = CityCollectionQuery[cityCollection];
      dispatch({
        type: RESULTS_REQUESTED
      });
      profilesSearchClient(searchQuery).then(([resp]) => {
        dispatch({
          type: RESULTS_RECEIVED,
          payload: resp
        });
      }).catch(() => {
        dispatch({
          type: RESULTS_FAILED
        });
      });
    }
  }, [cityCollection, cityParam, profilesSearchClient, requestState]);
  useEffect(() => {
    if (isSucceeded(requestState) && total > CityCollectionQuery.DEFAULT_LIMIT) {
      Raven.captureMessage('City collection limit reached', {
        level: 'warning',
        extra: {
          cityCollection,
          total
        }
      });
    }
  }, [cityCollection, requestState, total]);
  return /*#__PURE__*/_jsxs(UsageScreenProvider, {
    screen: "city page",
    subscreen: cityCollection,
    children: [isSucceeded(requestState) && /*#__PURE__*/_jsx(NavMarker, {
      name: "CITY_COLLECTION_LOAD"
    }), /*#__PURE__*/_jsx(PublicAppComponent, {
      children: /*#__PURE__*/_jsxs(DocumentHead, {
        children: [/*#__PURE__*/_jsx(MetaDescription, {
          content: metaDescriptionText
        }), /*#__PURE__*/_jsx(CanonicalLink, {
          href: canonicalUrl
        }), /*#__PURE__*/_jsx(MetaRobots, {
          noFollow: noFollowIndex,
          noIndex: noFollowIndex
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "card"
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "site"
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "image",
          content: socialImageUrl
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "type"
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "title"
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "url",
          content: canonicalUrl
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "description",
          content: metaDescriptionText
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "image",
          content: socialImageUrl
        }), /*#__PURE__*/_jsx(PrerenderReady, {
          ready: isFulfilled(requestState),
          statusCode: prerenderStatusCode
        })]
      })
    }), /*#__PURE__*/_jsx(HSPageViewTracker, {
      pathname: getFullLocationPathname(pathname, canonicalAppContext, lang)
    }), /*#__PURE__*/_jsx(HeaderSection, {
      children: /*#__PURE__*/_jsx(MainSectionInner, {
        style: {
          maxWidth: 992
        },
        children: /*#__PURE__*/_jsxs(MediaObject, {
          align: "center",
          itemLeft: /*#__PURE__*/_jsx(MediaQueryView, {
            min: ScreenSizes.MD,
            children: /*#__PURE__*/_jsx(Image, {
              src: headerImageUrl,
              marginBlockEnd: "400"
            })
          }),
          spacing: "extra-large",
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h1",
            marginBlockEnd: "400",
            color: "text-core-on-fill-default",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "ecosystem-marketplace-solutions-ui.cityCollection.header.title",
              options: {
                city: cityText
              }
            })
          }), /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "ecosystem-marketplace-solutions-ui.cityCollection.header.message",
            options: {
              city: cityText
            }
          })]
        })
      })
    }), /*#__PURE__*/_jsx("main", {
      children: /*#__PURE__*/_jsx(MainSection, {
        children: /*#__PURE__*/_jsxs(MainSectionInner, {
          style: {
            maxWidth: 992
          },
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h2",
            textAlign: "center",
            marginBlockEnd: "800",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "ecosystem-marketplace-solutions-ui.cityCollection.results.title",
              options: {
                city: cityText
              }
            })
          }), /*#__PURE__*/_jsx(LoadingWrapper, {
            ErrorComponent: ResultsErrorComponent,
            LoadingComponent: ResultsLoadingComponent,
            requestStatus: requestState,
            children: /*#__PURE__*/_jsxs(Fragment, {
              children: [/*#__PURE__*/_jsx(ResultsGrid, {
                results: results
              }), /*#__PURE__*/_jsx(ResultsCallout, {
                signUpUrl: getSignUpUrl(pathname)
              }), /*#__PURE__*/_jsx(UsageViewTracker, {
                properties: getUsageEventProperties
              })]
            })
          })]
        })
      })
    })]
  });
};
export default CityCollection;